// New component for the consolidated report 
// it handles columnm names dynamically and adjust each column accdording to q4 size
// copy of print reports
import React, { useState, useEffect } from "react";
import moment from "moment";
import { Redirect } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Button, Col, Row } from "reactstrap";
import Table from "react-bootstrap/Table";

var companySymbolCurrency = localStorage.getItem("companySymbolCurrency");
var customerName = localStorage.getItem("firstName");
var username = localStorage.getItem("username");
let currentDate = moment(new Date()).format("YYYY-MM-DD");




function ConsolidatedScreenTable({ data, headers, headerKeyMap }) {
  if (!data?.length) return <p>Loading data...</p>;

  const TOTAL_KEYWORDS = [
    "Total",
    "SubTotal",
    "Total Profits",
    "Product Wise Sales",
    "Stock Return",
    "U-Clip Stock Return",
  ];

  return (
    <Table
      striped
      bordered
      hover
      size="sm"
      style={{ fontSize: "0.78rem", tableLayout: "auto" }}
    >
      <thead>
        <tr style={{ backgroundColor: "#3f0e80", color: "#fff" }}>
          {headers.map((element, i) => {
            const label =
              typeof element === "object" && element?.value
                ? element.value
                : element;
            const align =
              typeof element === "object" && element?.align
                ? element.align
                : "center";
            return (
              <th key={i} style={{ textAlign: align, whiteSpace: "nowrap", color: "#fff" }}>
                {label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => {
          const secondKey = Object.keys(row)[1];
          const isBold = TOTAL_KEYWORDS.includes(row[secondKey]);
          return (
            <tr key={idx} style={{ fontWeight: isBold ? "bold" : "normal" }}>
              {headerKeyMap.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    textAlign: cell?.align ?? "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row[`${cell?.value}`]}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function buildConsolidatedPDF({
  reportName,
  criteriaString,
  headers,
  headerKeyMap,
  rawData,
  pdfDataArray,
  username,
  companyName,
}) {
  
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a2" });
  const PAGE_W = doc.internal.pageSize.getWidth();   // ~594 mm
  const PAGE_H = doc.internal.pageSize.getHeight();  // ~420 mm
  const MARGIN = 10;
  const FOOTER_Y = PAGE_H - 8;

  // ── 2. Normalise data (blank duplicate FINISHED_ITEM rows) ──
  const source = pdfDataArray ?? rawData;
  const rows = source.map((row, idx, arr) => {
    if (
      idx > 0 &&
      row.FINISHED_ITEM &&
      row.FINISHED_ITEM === arr[idx - 1].FINISHED_ITEM
    ) {
      return { ...row, INVOICE_DATE: "", INVOICE_NO: "", FINISHED_ITEM: "" };
    }
    return row;
  });

  // ── 3. Build header labels & column-style map ──────────────
  const headLabels = headers.map((h) =>
    typeof h === "object" && h?.value ? h.value : h
  );

  const colStyles = headerKeyMap.reduce((acc, item, i) => {
    acc[i] = { halign: item.align ?? "left" };
    return acc;
  }, {});

  // Build cells from headerKeyMap (same as the on-screen table) instead of
  // Object.values, so a row whose key order differs from the column list
  // cannot shift/swap data under the headers.
  const bodyRows = headerKeyMap?.length
    ? rows.map((item) =>
        headerKeyMap.map((cell) => item[`${cell?.value}`] ?? "")
      )
    : rows.map((item) => Object.values(item));

  // ── 4. Smart font-size calculation ────────────────────────
  //  jsPDF-autoTable uses ~2.8 px per pt at 72dpi.
  //  We calculate the minimum column widths and shrink the font
  //  until everything fits within PAGE_W - 2*MARGIN.
  const colCount = headLabels.length;
  const USABLE_W = PAGE_W - MARGIN * 2;       // mm available for table
  const MM_PER_PT = 0.3528;                   // 1pt = 0.3528 mm
  const CHAR_RATIO = 0.55;                    // approximate char-width / font-size ratio

  // Measure the widest content per column (header + all rows)
  const maxCharsPerCol = headLabels.map((label, ci) => {
    const headerLen = String(label).length;
    const dataLen = Math.max(
      0,
      ...bodyRows.map((r) => String(r[ci] ?? "").length)
    );
    return Math.max(headerLen, dataLen);
  });

  // Binary-search the largest font size (between 4pt and 14pt)
  // where total min-width ≤ USABLE_W
  let fontSize = 14;
  for (let fs = 14; fs >= 4; fs -= 0.5) {
    const charWidthMM = fs * MM_PER_PT * CHAR_RATIO;
    const cellPadMM = 1.5; // padding left + right per cell (mm)
    const totalW = maxCharsPerCol.reduce(
      (sum, chars) => sum + chars * charWidthMM + cellPadMM * 2,
      0
    );
    if (totalW <= USABLE_W) {
      fontSize = fs;
      break;
    }
  }

  // ── 5. Titles ──────────────────────────────────────────────
  const TITLE_FS = Math.min(18, fontSize * 1.6);
  const CRIT_FS = Math.min(14, fontSize * 1.3);

  doc.setFontSize(TITLE_FS);
  doc.text(reportName, MARGIN, 12);
  doc.setFontSize(CRIT_FS);
  doc.text(criteriaString ?? "All", MARGIN, 20);

  // ── 6. autoTable call ─────────────────────────────────────
  const BOLD_KEYS = [
    "Total",
    "SubTotal",
    "Total Profits",
    "Product Wise Sales",
    "Stock Return",
    "U-Clip Stock Return",
  ];

  doc.autoTable({
    head: [headLabels],
    body: bodyRows,
    startY: 24,
    theme: "striped",
    styles: {
      fontSize,
      cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
      overflow: "linebreak",   // wrap instead of truncate
      valign: "middle",
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [63, 14, 128],
      textColor: 255,
      halign: "center",
      valign: "middle",
      fontStyle: "bold",
      fontSize: Math.max(fontSize, 5), // headers never smaller than 5pt
    },
    columnStyles: colStyles,
    tableWidth: "auto",          // let autoTable decide widths
    margin: { left: MARGIN, right: MARGIN },

    // ── Footer on every page ────────────────────────────────
    didDrawPage: () => {
      const today = new Date().toLocaleDateString("en-US");
      doc.setFontSize(Math.min(10, fontSize));
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
        MARGIN,
        FOOTER_Y
      );
      doc.text(`Generated By: ${username}`, PAGE_W / 2 - 30, FOOTER_Y);
      doc.text(`Generated on: ${today}`, PAGE_W - 60, FOOTER_Y);
    },

    // ── Bold totals / subtotals ─────────────────────────────
    didParseCell: (data) => {
      const r = data.row.raw;
      if (!r) return;
      const isBold =
        r[2] === "Sub Total" ||
        r[0] === "Total" ||
        BOLD_KEYS.includes(r[1]);
      if (isBold) data.cell.styles.fontStyle = "bold";
    },
  });

  doc.save(reportName);
}

export default function ConsolidatedStockReportPrint(props) {
  const locationState = props?.location?.state ?? {};

  const [screenData, setScreenData] = useState([]);
  const [redirectToLogin, setRedirectToLogin] = useState(false);
  const [companyName] = useState(localStorage.getItem("companyName") ?? "");

  const {
    dynamicInvMessage,
    reportName,
    criteriaString = "All",
    headers = [],
    headerKeyMap = [],
    data: rawData,
    pdfDataArray,
    goBackUrl,
  } = locationState;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setRedirectToLogin(true);
      return;
    }

    const fallbackUrl = "/ConsolidatedStockReport";

    if (!rawData) {
      props.history.push({ pathname: fallbackUrl });
      return;
    }

  
    const modified = rawData.map((row, idx, arr) => {
      if (
        idx > 0 &&
        row.master_invoice_id === arr[idx - 1].master_invoice_id &&
        row.FINISHED_ITEM === arr[idx - 1].FINISHED_ITEM &&
        row.master_invoice_id > -1
      ) {
        return { ...row, INVOICE_DATE: "", INVOICE_NO: "", FINISHED_ITEM: "" };
      }
      return row;
    });
    setScreenData(modified);
  }, []); 

  if (redirectToLogin) return <Redirect to="/login" />;

  const handleGoBack = () =>
    props.history.push({ pathname: goBackUrl});

  const handleDownloadPDF = () => {
    buildConsolidatedPDF({
      reportName,
      criteriaString,
      headers,
      headerKeyMap,
      rawData,
      pdfDataArray,
      username: username,
      companyName,
    });
  };


  return (
    <Row id="app-body-Invoice" className="app-body-Invoice mt-3">
      <Col xs="12">
     
        <Row className="align-items-center mb-2">
          <Col xs="6">
            <h2 style={{ marginLeft: 5 }}>{companyName}</h2>
          </Col>

          <Col xs="4">
            <span style={{ marginLeft: "13em" }}>
              Printed By :{" "}
              <span className="text-success"> {customerName} </span>
            </span>
          </Col>

          <Col xs="2">
            <span>
              Printed Date :{" "}
              <span className="text-success"> {currentDate} </span>
            </span>
          </Col>

          <Col xs="8">
            <h3 style={{ marginLeft: 5 }}>{dynamicInvMessage}</h3>
          </Col>

          <Col xs="4">
            <div style={{ textAlign: "end" }}>
              <Button className="btn btn-warning mx-2" onClick={handleDownloadPDF}>
                Print PDF
              </Button>
              <button
                className="btn btn-secondary m-1"
                onClick={handleGoBack}
              >
                Back
              </button>
            </div>
          </Col>

          <Col xs="12">
            <p style={{ marginLeft: 5 }}>
              <b>Data Criteria: {criteriaString}</b>
            </p>
          </Col>
        </Row>

        {/* ── Data table ───────────────────────────────────── */}
        <Row>
          <Col xs="12" style={{ overflowX: "auto" }}>
            <ConsolidatedScreenTable
              data={screenData}
              headers={headers}
              headerKeyMap={headerKeyMap}
            />
          </Col>
        </Row>

        {/* ── Currency note ────────────────────────────────── */}
        <Row>
          <Col xs="12">
            <p>
              *{" "}
              <span className="text-danger">Note</span>: All figures indicated
              are in the company's currency (
              <span className="text-success"> {companySymbolCurrency} </span>).
            </p>
          </Col>
        </Row>
        <br />
      </Col>
    </Row>
  );
}